import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { RBACProvider } from './contexts/RBACContext';
import { PortalProvider, usePortal } from './contexts/PortalContext';
import { GlobalContextProvider } from './contexts/GlobalContext';
import './App.css';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';

// Portal Pages
import PortalLoginPage from './pages/portal/PortalLoginPage';

// Compliance Pages
import ROPAPage from './pages/compliance/ropa/ROPAPage';
import ROPAFormPage from './pages/compliance/ropa/ROPAFormPage';
import ROPAViewPage from './pages/compliance/ropa/ROPAViewPage';
import DataBreachPage from './pages/compliance/breach/DataBreachPage';
import BreachWorkflowPage from './pages/compliance/breach/BreachWorkflowPage';
import DSRPageV2 from './pages/compliance/dsr/DSRPageV2';
import DSRWorkflowPage from './pages/compliance/dsr/DSRWorkflowPage';
// DEPRECATED (Mar 5, 2026): Legacy vendor pages - use VendorRegistryPage, SimplifiedVendorWorkflow instead
// import VendorPageV2 from './pages/compliance/vendor/VendorPageV2';
// import VendorWorkflowPage from './pages/compliance/vendor/VendorWorkflowPage';
// import SimplifiedVendorList from './pages/compliance/vendor/SimplifiedVendorList'; // DEPRECATED (Apr 2, 2026): use VendorRegistryPage
import VendorRegistryPage from './pages/compliance/vendor/VendorRegistryPage';
import SimplifiedVendorWorkflow from './pages/compliance/vendor/SimplifiedVendorWorkflow';
import DueDiligenceList from './pages/compliance/vendor/DueDiligenceList';
import DueDiligenceWorkflow from './pages/compliance/vendor/DueDiligenceWorkflow';

// Risk Pages
import RiskAssessmentsPage from './pages/risk/RiskAssessmentsPage';
import RiskRegisterPage from './pages/risk/RiskRegisterPage';
import DPIAWorkflowPage from './pages/risk/DPIAWorkflowPage';
import AIAssessmentWorkflowPage from './pages/risk/AIAssessmentWorkflowPage';
import DPIAIntakeManagementPage from './pages/risk/DPIAIntakeManagementPage';
import LIAWorkflowPage from './pages/risk/LIAWorkflowPage';
import TIAWorkflowPage from './pages/risk/TIAWorkflowPage';

// Public Pages (No Auth)
import DPIAIntakeFormPage from './pages/public/DPIAIntakeFormPage';
import DPIAIntakeSuccessPage from './pages/public/DPIAIntakeSuccessPage';
import BreachIntakeFormPage from './pages/public/BreachIntakeFormPage';
import BreachIntakeSuccessPage from './pages/public/BreachIntakeSuccessPage';
import PreScreenFormPage from './pages/public/PreScreenFormPage';
import ROPAIntakeFormPage from './pages/public/ROPAIntakeFormPage';
import ROPAIntakeSuccessPage from './pages/public/ROPAIntakeSuccessPage';
import PreScreenDetailPage from './pages/risk/prescreen/PreScreenDetailPage';

// Maturity Assessment Pages
import MaturityDashboardPage from './pages/compliance/maturity/MaturityDashboardPage';
import MaturityDomainPage from './pages/compliance/maturity/MaturityDomainPage';
import MaturityControlPage from './pages/compliance/maturity/MaturityControlPage';
import MaturityReviewQueuePage from './pages/compliance/maturity/MaturityReviewQueuePage';

// Questionnaire Pages
import QuestionnaireListPage from './pages/compliance/questionnaire/QuestionnaireListPage';
import CampaignCreatePage from './pages/compliance/questionnaire/CampaignCreatePage';
import CampaignDetailPage from './pages/compliance/questionnaire/CampaignDetailPage';
import QuestionnaireResponsePage from './pages/compliance/questionnaire/QuestionnaireResponsePage';

// Readiness Assessment Pages
import ReadinessListPage from './pages/compliance/readiness/ReadinessListPage';
import ReadinessCreatePage from './pages/compliance/readiness/ReadinessCreatePage';
import ReadinessDetailPage from './pages/compliance/readiness/ReadinessDetailPage';
import ReadinessAssessmentPage from './pages/public/ReadinessAssessmentPage';

// Operations Pages
import TasksPage from './pages/operations/TasksPage';
import DocumentsPage from './pages/operations/DocumentsPage';

// Admin Pages
import UserManagementPage from './pages/admin/UserManagementPage';
import AuditTrailPage from './pages/admin/AuditTrailPage';
import RiskSettingsPage from './pages/admin/RiskSettingsPage';
import ClientManagementPage from './pages/admin/ClientManagementPage';
import EntityManagementPage from './pages/admin/EntityManagementPage';
import SystemSettingsPage from './pages/admin/SystemSettingsPage';
import SuperAdminMFAPage from './pages/admin/SuperAdminMFAPage';
import OrgMFAPage from './pages/admin/OrgMFAPage';
import MFAVerifyPage from './pages/auth/MFAVerifyPage';
import MFASetupPage from './pages/auth/MFASetupPage';
import SecurityPage from './pages/account/SecurityPage';
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import SetPasswordPage from './pages/auth/SetPasswordPage';
import VerifyEmailChangePage from './pages/auth/VerifyEmailChangePage';

// Guest Pages
import GuestLoginPage from './pages/guest/GuestLoginPage';
import GuestPortalPage from './pages/guest/GuestPortalPage';
import TempGuestManagementPage from './pages/admin/TempGuestManagementPage';

// Settings Pages
import NotificationPreferencesPage from './pages/settings/NotificationPreferencesPage';
import ClientBrandingPage from './pages/settings/ClientBrandingPage';
import EmailSuppressionPage from './pages/admin/EmailSuppressionPage';

// New Compliance Pages
import DataFlowMapPage from './pages/compliance/DataFlowMapPage';

// Placeholder components for routes not yet implemented
import { DashboardLayout } from './components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Construction } from 'lucide-react';

const PlaceholderPage = ({ title }) => (
  <DashboardLayout>
    <div className="p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Construction className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">This module is under development and will be available soon.</p>
        </CardContent>
      </Card>
    </div>
  </DashboardLayout>
);

// Protected Route wrapper
const ProtectedRoute = ({ children, roles }) => {
  const { isAuthenticated, loading, user, mfaEnforcement } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Temp guests must stay in the guest portal — they have a valid token but no dashboard access
  if (user?.role === 'temp_guest') {
    return <Navigate to="/guest/portal" replace />;
  }

  // MFA enforcement gate: redirect to security page if org requires MFA but user hasn't set it up
  // Allow access to the security page itself so the user can complete setup
  if (
    mfaEnforcement?.must_setup &&
    user?.role !== 'super_admin' &&
    location.pathname !== '/account/security'
  ) {
    return <Navigate to="/account/security" replace />;
  }

  // Super admin can access everything
  if (user?.role === 'super_admin') {
    return children;
  }

  if (roles && !roles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Guest Route wrapper — only allows temp_guest sessions
const GuestRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  const isGuest = localStorage.getItem('guest_mode') === 'true';
  if (!token || !isGuest) {
    return <Navigate to="/guest/login" replace />;
  }
  // Check client-side expiry from stored guest user
  try {
    const guestUser = JSON.parse(localStorage.getItem('guest_user') || '{}');
    if (guestUser.guest_expires_at && new Date(guestUser.guest_expires_at) < new Date()) {
      localStorage.removeItem('token');
      localStorage.removeItem('guest_mode');
      localStorage.removeItem('guest_user');
      return <Navigate to="/guest/login" replace />;
    }
  } catch {}
  return children;
};

// Public Route wrapper (redirect if authenticated, show portal login if in portal mode)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const { isPortalMode, isLoading: portalLoading } = usePortal();

  if (loading || portalLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show portal login page if in portal mode
  if (isPortalMode) {
    return <PortalLoginPage />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
      <Route path="/set-password" element={<PublicRoute><SetPasswordPage /></PublicRoute>} />
      <Route path="/verify-email-change" element={<PublicRoute><VerifyEmailChangePage /></PublicRoute>} />
      <Route path="/mfa/verify" element={<MFAVerifyPage />} />
      <Route path="/mfa/setup" element={<MFASetupPage />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      
      {/* DPO Only - Clients */}
      <Route path="/clients" element={<ProtectedRoute roles={['dpo']}><ClientsPage /></ProtectedRoute>} />

      {/* Compliance Routes */}
      <Route path="/compliance/ropa" element={<ProtectedRoute><ROPAPage /></ProtectedRoute>} />
      <Route path="/compliance/ropa/new" element={<ProtectedRoute><ROPAFormPage /></ProtectedRoute>} />
      <Route path="/compliance/ropa/edit/:id" element={<ProtectedRoute><ROPAFormPage /></ProtectedRoute>} />
      <Route path="/compliance/ropa/view/:id" element={<ProtectedRoute><ROPAViewPage /></ProtectedRoute>} />
      <Route path="/compliance/data-flows" element={<ProtectedRoute><DataFlowMapPage /></ProtectedRoute>} />
      <Route path="/compliance/breaches" element={<ProtectedRoute><DataBreachPage /></ProtectedRoute>} />
      <Route path="/compliance/breaches/workflow/new" element={<ProtectedRoute><BreachWorkflowPage /></ProtectedRoute>} />
      <Route path="/compliance/breaches/workflow/:id" element={<ProtectedRoute><BreachWorkflowPage /></ProtectedRoute>} />
      <Route path="/compliance/dsr" element={<ProtectedRoute><DSRPageV2 /></ProtectedRoute>} />
      <Route path="/compliance/dsr/workflow/new" element={<ProtectedRoute><DSRWorkflowPage /></ProtectedRoute>} />
      <Route path="/compliance/dsr/workflow/:id" element={<ProtectedRoute><DSRWorkflowPage /></ProtectedRoute>} />
      <Route path="/compliance/maturity" element={<ProtectedRoute><MaturityDashboardPage /></ProtectedRoute>} />
      <Route path="/compliance/maturity/domain/:domainCode" element={<ProtectedRoute><MaturityDomainPage /></ProtectedRoute>} />
      <Route path="/compliance/maturity/control/:controlId" element={<ProtectedRoute><MaturityControlPage /></ProtectedRoute>} />
      <Route path="/compliance/maturity/review-queue/:entityId" element={<ProtectedRoute><MaturityReviewQueuePage /></ProtectedRoute>} />
      
      {/* Vendor Routes - Simplified (Default) */}
      <Route path="/compliance/vendors" element={<ProtectedRoute><VendorRegistryPage /></ProtectedRoute>} />
      <Route path="/compliance/vendors/simplified/new" element={<ProtectedRoute><SimplifiedVendorWorkflow /></ProtectedRoute>} />
      <Route path="/compliance/vendors/simplified/:id" element={<ProtectedRoute><SimplifiedVendorWorkflow /></ProtectedRoute>} />
      
      {/* Due Diligence Routes */}
      <Route path="/compliance/due-diligence" element={<ProtectedRoute><DueDiligenceList /></ProtectedRoute>} />
      <Route path="/compliance/due-diligence/new" element={<ProtectedRoute><DueDiligenceWorkflow /></ProtectedRoute>} />
      <Route path="/compliance/due-diligence/:id" element={<ProtectedRoute><DueDiligenceWorkflow /></ProtectedRoute>} />
      
      {/* DEPRECATED (Mar 5, 2026): Legacy vendor routes - use SimplifiedVendorList instead */}
      {/* <Route path="/compliance/vendors/legacy" element={<ProtectedRoute><VendorPageV2 /></ProtectedRoute>} /> */}
      {/* <Route path="/compliance/vendors/workflow/new" element={<ProtectedRoute><VendorWorkflowPage /></ProtectedRoute>} /> */}
      {/* <Route path="/compliance/vendors/workflow/:id" element={<ProtectedRoute><VendorWorkflowPage /></ProtectedRoute>} /> */}

      {/* Risk Routes */}
      <Route path="/risk/assessments" element={<ProtectedRoute><RiskAssessmentsPage /></ProtectedRoute>} />
      <Route path="/risk/dpia/new" element={<ProtectedRoute><DPIAWorkflowPage /></ProtectedRoute>} />
      <Route path="/risk/dpia/:id" element={<ProtectedRoute><DPIAWorkflowPage /></ProtectedRoute>} />
      <Route path="/risk/dpia-intake" element={<ProtectedRoute><DPIAIntakeManagementPage /></ProtectedRoute>} />
      <Route path="/risk/dpia-intake/:intakeId/form" element={<ProtectedRoute><DPIAIntakeFormPage dpoMode /></ProtectedRoute>} />
      <Route path="/risk/ai/new" element={<ProtectedRoute><AIAssessmentWorkflowPage /></ProtectedRoute>} />
      <Route path="/risk/ai/:id" element={<ProtectedRoute><AIAssessmentWorkflowPage /></ProtectedRoute>} />
      <Route path="/risk/lia/new" element={<ProtectedRoute><LIAWorkflowPage /></ProtectedRoute>} />
      <Route path="/risk/lia/:id" element={<ProtectedRoute><LIAWorkflowPage /></ProtectedRoute>} />
      <Route path="/risk/tia/new" element={<ProtectedRoute><TIAWorkflowPage /></ProtectedRoute>} />
      <Route path="/risk/tia/:id" element={<ProtectedRoute><TIAWorkflowPage /></ProtectedRoute>} />
      <Route path="/risk/register" element={<ProtectedRoute><RiskRegisterPage /></ProtectedRoute>} />


      {/* DPO Pre-Screen Detail (requires auth) */}
      <Route path="/risk/prescreen/:id" element={<PreScreenDetailPage />} />


      {/* Questionnaire Routes */}
      <Route path="/compliance/questionnaires" element={<ProtectedRoute><QuestionnaireListPage /></ProtectedRoute>} />
      <Route path="/compliance/questionnaires/campaign/new" element={<ProtectedRoute><CampaignCreatePage /></ProtectedRoute>} />
      <Route path="/compliance/questionnaires/campaign/:campaignId" element={<ProtectedRoute><CampaignDetailPage /></ProtectedRoute>} />
      

      {/* Readiness Assessment Routes */}
      <Route path="/compliance/readiness" element={<ProtectedRoute><ReadinessListPage /></ProtectedRoute>} />
      <Route path="/compliance/readiness/new" element={<ProtectedRoute><ReadinessCreatePage /></ProtectedRoute>} />
      <Route path="/compliance/readiness/:id" element={<ProtectedRoute><ReadinessDetailPage /></ProtectedRoute>} />
      

      {/* Operations Routes */}
      <Route path="/operations/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
      <Route path="/operations/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />

      {/* Admin Routes */}
      <Route path="/admin/clients" element={<ProtectedRoute roles={['dpo']}><ClientManagementPage /></ProtectedRoute>} />
      <Route path="/admin/clients/:clientId/entities" element={<ProtectedRoute roles={['dpo', 'client_admin']}><EntityManagementPage /></ProtectedRoute>} />
      <Route path="/admin/entities" element={<ProtectedRoute roles={['dpo', 'client_admin']}><EntityManagementPage /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute roles={['dpo', 'client_admin', 'entity_admin']}><UserManagementPage /></ProtectedRoute>} />
      <Route path="/admin/audit" element={<ProtectedRoute roles={['super_admin']}><AuditTrailPage /></ProtectedRoute>} />
      <Route path="/admin/reports" element={<ProtectedRoute roles={['dpo']}><PlaceholderPage title="Reports" /></ProtectedRoute>} />
      <Route path="/admin/risk-settings" element={<ProtectedRoute roles={['super_admin']}><RiskSettingsPage /></ProtectedRoute>} />
      <Route path="/admin/system-settings" element={<ProtectedRoute roles={['super_admin']}><SystemSettingsPage /></ProtectedRoute>} />
      <Route path="/admin/platform" element={<ProtectedRoute roles={['super_admin']}><SuperAdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/mfa" element={<ProtectedRoute roles={['super_admin']}><SuperAdminMFAPage /></ProtectedRoute>} />
      <Route path="/admin/org-mfa" element={<ProtectedRoute roles={['dpo', 'client_admin', 'entity_admin']}><OrgMFAPage /></ProtectedRoute>} />
      <Route path="/account/security" element={<ProtectedRoute><SecurityPage /></ProtectedRoute>} />

      {/* Settings Routes */}
      <Route path="/settings/notifications" element={<ProtectedRoute><NotificationPreferencesPage /></ProtectedRoute>} />
      <Route path="/settings/branding" element={<ProtectedRoute roles={['super_admin']}><ClientBrandingPage /></ProtectedRoute>} />
      <Route path="/admin/email-suppression" element={<ProtectedRoute roles={['dpo']}><EmailSuppressionPage /></ProtectedRoute>} />
      <Route path="/admin/temp-guests" element={<ProtectedRoute roles={['dpo', 'super_admin', 'client_admin', 'entity_admin']}><TempGuestManagementPage /></ProtectedRoute>} />

      {/* Guest Routes */}
      <Route path="/guest/login" element={<GuestLoginPage />} />
      <Route path="/guest/portal" element={<GuestRoute><GuestPortalPage /></GuestRoute>} />
      {/* Guest form routes — reuse existing public form pages but behind guest auth */}
      <Route path="/guest/ropa-intake" element={<GuestRoute><ROPAIntakeFormPage guestMode /></GuestRoute>} />
      <Route path="/guest/breach-intake" element={<GuestRoute><BreachIntakeFormPage guestMode /></GuestRoute>} />
      <Route path="/guest/dpia-intake" element={<GuestRoute><DPIAIntakeFormPage guestMode /></GuestRoute>} />
      <Route path="/guest/prescreen" element={<GuestRoute><PreScreenFormPage guestMode /></GuestRoute>} />
      <Route path="/guest/readiness-assessment" element={<GuestRoute><ReadinessAssessmentPage guestMode /></GuestRoute>} />
      <Route path="/guest/questionnaire" element={<GuestRoute><QuestionnaireResponsePage guestMode /></GuestRoute>} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <RBACProvider>
          <PortalProvider>
            <GlobalContextProvider>
              <BrowserRouter>
                <AppRoutes />
                <Toaster position="top-right" richColors closeButton />
              </BrowserRouter>
            </GlobalContextProvider>
          </PortalProvider>
        </RBACProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
